import { useState } from 'react';
import {
    CircularProgress,
    IconButton,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export interface AccionMenuFila {
    /** Texto del ítem. */
    texto: string;
    /** Nombre accesible: el texto más el CUIT, como tenían los botones. */
    etiqueta: string;
    onClick: () => void;
    /** Rojo para lo destructivo (Cancelar en SRT). */
    destructiva?: boolean;
    deshabilitada?: boolean;
    cargando?: boolean;
    /** Por qué está deshabilitada, en pocas palabras: va debajo del texto. */
    motivo?: string;
}

interface MenuAccionesFilaProps {
    cuit: string;
    acciones: AccionMenuFila[];
}

/**
 * Las acciones de la fila que no son la principal (Ver proceso, Notificar
 * cliente, Cancelar en SRT) van a un menú ⋮: apiladas como botones hacían
 * filas de 220 px. Las reglas de habilitación no cambian; un ítem
 * deshabilitado dice por qué debajo del texto, en vez de en un tooltip.
 */
const MenuAccionesFila = ({ cuit, acciones }: MenuAccionesFilaProps) => {
    const [ancla, setAncla] = useState<HTMLElement | null>(null);
    if (!acciones.length) return null;

    const cerrar = () => setAncla(null);

    return (
        <>
            <Tooltip title="Más acciones">
                <IconButton
                    aria-label={`Más acciones — CUIT ${cuit}`}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(ancla)}
                    onClick={(evento) => setAncla(evento.currentTarget)}
                    sx={{
                        width: 32,
                        height: 32,
                        flexShrink: 0,
                        borderRadius: '8px',
                        border: '1px solid #E0EBED',
                        bgcolor: ancla ? '#F3F8F8' : '#FFFFFF'
                    }}
                >
                    <MoreVertIcon sx={{ fontSize: 18, color: '#3D4A50' }} />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={ancla}
                open={Boolean(ancla)}
                onClose={cerrar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            minWidth: 220,
                            borderRadius: '12px',
                            border: '1px solid #E0EBED',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.16)'
                        }
                    }
                }}
                MenuListProps={{ sx: { p: 0.75 } }}
            >
                {acciones.map((accion) => (
                    <MenuItem
                        key={accion.texto}
                        aria-label={accion.etiqueta}
                        disabled={accion.deshabilitada}
                        onClick={() => {
                            cerrar();
                            accion.onClick();
                        }}
                        sx={{
                            borderRadius: '8px',
                            gap: 1,
                            py: 1,
                            color: accion.destructiva ? 'error.main' : 'text.primary'
                        }}
                    >
                        <ListItemText
                            primary={accion.texto}
                            secondary={accion.motivo}
                            primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                            secondaryTypographyProps={{ fontSize: 11 }}
                        />
                        {accion.cargando && <CircularProgress size={14} />}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default MenuAccionesFila;
